import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useActiveIModelConnection } from "@itwin/appui-react";
import { getBackendBase } from "../trimble/coreFiles";
import { useTrimbleConnect } from "../trimble/useTrimbleConnect";

const ADMIN_MODE_QUERY_PARAM = "ncAdmin";
const ADMIN_MODE_STORAGE_KEY = "niceconnect.monitoringAdminEnabled";
const STANDALONE_ADMIN_OVERRIDE_QUERY_PARAM = "ncStandaloneAdmin";
const STANDALONE_ADMIN_OVERRIDE_STORAGE_KEY = "niceconnect.monitoringAdminStandaloneEnabled";
const ADMIN_SECRET_STORAGE_KEY = "niceconnect.monitoringAdminSecret";

type MonitoringDiagnostics = {
  iTwinId?: string;
  iModelId?: string;
  status?: string;
  summary?: string;
  signals?: Record<string, unknown>;
  subscription?: {
    subscriptionId?: string;
    enabled?: boolean;
    notificationRecipients?: string[];
    lastProcessedChangesetId?: string;
    lastProcessedChangesetIndex?: number | null;
    lastSeenChangesetId?: string;
    lastSeenChangesetIndex?: number | null;
  } | null;
  latestReceivedEvent?: {
    webhookId?: string;
    changesetId?: string;
    changesetIndex?: number | null;
    receivedAt?: string;
  } | null;
  latestDeltaJob?: {
    jobId?: string;
    status?: string;
    toChangesetId?: string;
    toChangesetIndex?: number | null;
    updatedAt?: string;
  } | null;
  webhook?: {
    matchingWebhook?: {
      webhookId?: string;
      active?: boolean;
    } | null;
    callbackUrl?: string;
    lookupError?: string | null;
  } | null;
  polling?: {
    enabled?: boolean;
    intervalMs?: number;
    lastRunStartedAt?: string | null;
    lastRunCompletedAt?: string | null;
    lastSummary?: {
      erroredCount?: number;
      unchangedCount?: number;
      queuedDeltaJobCount?: number;
      checkedSubscriptionCount?: number;
      subscriptionOutcomes?: Array<Record<string, unknown>>;
    } | null;
  } | null;
  bentleyServiceApp?: {
    latestVisibleChangeset?: {
      changesetId?: string;
      changesetIndex?: number | null;
      description?: string;
      pushDateTime?: string;
    } | null;
    latestVisibleChangesetError?: string | null;
  } | null;
  recommendations?: string[];
};

type MonitoringDiagnosticsResponse = {
  ok?: boolean;
  route?: string;
  diagnostics?: MonitoringDiagnostics;
};

function isTruthyToken(value: string | null | undefined): boolean {
  return /^(1|true|yes|on)$/i.test(String(value ?? "").trim());
}

function isEmbeddedWindow(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window !== window.parent;
  } catch {
    return false;
  }
}

function readBooleanQueryOrSession(queryParam: string, storageKey: string): boolean {
  if (typeof window === "undefined") return false;

  try {
    const url = new URL(window.location.href);
    const raw = url.searchParams.get(queryParam);
    if (raw != null) {
      if (isTruthyToken(raw)) {
        window.sessionStorage.setItem(storageKey, "true");
        return true;
      }

      window.sessionStorage.removeItem(storageKey);
      return false;
    }

    return window.sessionStorage.getItem(storageKey) === "true";
  } catch {
    return false;
  }
}

function shouldEnableAdminModeFromBrowser(): boolean {
  return readBooleanQueryOrSession(ADMIN_MODE_QUERY_PARAM, ADMIN_MODE_STORAGE_KEY);
}

function shouldEnableStandaloneAdminOverrideFromBrowser(): boolean {
  return readBooleanQueryOrSession(
    STANDALONE_ADMIN_OVERRIDE_QUERY_PARAM,
    STANDALONE_ADMIN_OVERRIDE_STORAGE_KEY
  );
}

export function shouldShowMonitoringAdminWidget(): boolean {
  const adminRequested = shouldEnableAdminModeFromBrowser();
  if (!adminRequested) return false;
  return isEmbeddedWindow() || shouldEnableStandaloneAdminOverrideFromBrowser();
}

function readStoredAdminSecret(): string {
  if (typeof window === "undefined") return "";
  try {
    return String(window.sessionStorage.getItem(ADMIN_SECRET_STORAGE_KEY) ?? "").trim();
  } catch {
    return "";
  }
}

function storeAdminSecret(value: string) {
  if (typeof window === "undefined") return;
  try {
    const normalized = String(value ?? "").trim();
    if (!normalized) {
      window.sessionStorage.removeItem(ADMIN_SECRET_STORAGE_KEY);
      return;
    }
    window.sessionStorage.setItem(ADMIN_SECRET_STORAGE_KEY, normalized);
  } catch {
    // Ignore storage failures.
  }
}

async function callMonitoringAdminApi<T>(
  path: string,
  adminSecret: string,
  options: {
    method?: "GET" | "POST";
    body?: Record<string, unknown>;
  } = {}
): Promise<T> {
  const backendBase = getBackendBase();
  if (!backendBase) {
    throw new Error("NiceConnect backend base is not configured.");
  }

  const normalizedSecret = String(adminSecret ?? "").trim();
  if (!normalizedSecret) {
    throw new Error("Admin secret is required.");
  }

  const response = await fetch(`${backendBase.replace(/\/$/, "")}${path}`, {
    method: options.method ?? "GET",
    headers: {
      "x-niceconnect-admin-secret": normalizedSecret,
      ...(options.body ? { "Content-Type": "application/json" } : {}),
    },
    ...(options.body ? { body: JSON.stringify(options.body) } : {}),
  });

  const text = await response.text();
  let json: any = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }

  if (!response.ok) {
    throw new Error(json?.error ?? text ?? `Request failed (${response.status}).`);
  }

  return (json ?? {}) as T;
}

function statusBadgeColor(status: string): string {
  if (status === "healthy") return "#0f766e";
  if (status === "warning") return "#b45309";
  if (status === "error") return "#b42318";
  return "#475467";
}

function formatSignalValue(value: unknown): string {
  if (typeof value === "boolean") return value ? "true" : "false";
  if (value == null) return "n/a";
  return String(value);
}

export const MonitoringAdminWidget: React.FC = () => {
  const iModelConnection = useActiveIModelConnection();
  const trimble = useTrimbleConnect();
  const [adminSecret, setAdminSecret] = useState(() => readStoredAdminSecret());
  const [iTwinId, setITwinId] = useState("");
  const [iModelId, setIModelId] = useState("");
  const [diagnostics, setDiagnostics] = useState<MonitoringDiagnostics | null>(null);
  const [lastActionPayload, setLastActionPayload] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoadingDiagnostics, setIsLoadingDiagnostics] = useState(false);
  const [activeAction, setActiveAction] = useState<string>("");
  const [forceWebhookRecreate, setForceWebhookRecreate] = useState(false);
  const [useTapCallback, setUseTapCallback] = useState(false);
  const [omitWebhookSecret, setOmitWebhookSecret] = useState(false);

  useEffect(() => {
    setITwinId(String((iModelConnection as any)?.iTwinId ?? "").trim());
    setIModelId(String((iModelConnection as any)?.iModelId ?? "").trim());
  }, [iModelConnection]);

  useEffect(() => {
    storeAdminSecret(adminSecret);
  }, [adminSecret]);

  const backendBase = getBackendBase();
  const tapCallbackUrl = backendBase
    ? `${backendBase.replace(/\/$/, "")}/events/bentley-tap`
    : "";
  const embedded = isEmbeddedWindow();
  const standaloneOverrideEnabled = shouldEnableStandaloneAdminOverrideFromBrowser();
  const trimbleSessionReady = trimble.status === "ready" && Boolean(trimble.project?.id);
  const adminContextSummary = embedded
    ? trimbleSessionReady
      ? `Embedded in Trimble Connect (${trimble.project?.name ?? trimble.project?.id ?? "project loaded"}).`
      : `Embedded context detected, but Trimble session is ${trimble.status}.`
    : standaloneOverrideEnabled
      ? "Standalone admin override is enabled for this browser session."
      : "Standalone admin override is not enabled.";

  const canLoadDiagnostics = useMemo(
    () => Boolean(backendBase && adminSecret.trim() && iTwinId.trim() && iModelId.trim()),
    [adminSecret, backendBase, iModelId, iTwinId]
  );

  const loadDiagnostics = useCallback(async () => {
    if (!canLoadDiagnostics) {
      setError("Backend base, admin secret, iTwinId, and iModelId are required.");
      return;
    }

    setIsLoadingDiagnostics(true);
    setError(null);
    try {
      const response = await callMonitoringAdminApi<MonitoringDiagnosticsResponse>(
        `/monitoring/diagnostics?iTwinId=${encodeURIComponent(iTwinId)}&iModelId=${encodeURIComponent(iModelId)}`,
        adminSecret
      );
      setDiagnostics(response.diagnostics ?? null);
      setLastActionPayload(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsLoadingDiagnostics(false);
    }
  }, [adminSecret, canLoadDiagnostics, iModelId, iTwinId]);

  const runAction = useCallback(
    async (actionName: string, path: string, body?: Record<string, unknown>) => {
      if (!adminSecret.trim()) {
        setError("Admin secret is required.");
        return;
      }

      setActiveAction(actionName);
      setError(null);
      try {
        const payload = await callMonitoringAdminApi<any>(path, adminSecret, {
          method: "POST",
          body,
        });
        setLastActionPayload(payload);
        await loadDiagnostics();
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setActiveAction("");
      }
    },
    [adminSecret, loadDiagnostics]
  );

  const status = String(diagnostics?.status ?? "").trim();
  const statusColor = statusBadgeColor(status);
  const signalEntries = Object.entries(diagnostics?.signals ?? {});
  const recommendations = Array.isArray(diagnostics?.recommendations)
    ? diagnostics.recommendations
    : [];

  return (
    <div
      style={{
        display: "grid",
        gap: 12,
        padding: 12,
        fontSize: 12,
        lineHeight: 1.45,
      }}
    >
      <div style={{ display: "grid", gap: 4 }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>Monitoring Admin</div>
        <div style={{ color: "#475467" }}>
          Embedded admin uses <code>?ncAdmin=1</code>. Standalone testing requires
          <code> ?ncAdmin=1&amp;ncStandaloneAdmin=1</code>.
        </div>
        <div style={{ color: "#475467" }}>
          Backend: <code>{backendBase ?? "not configured"}</code>
        </div>
        <div style={{ color: standaloneOverrideEnabled && !embedded ? "#b45309" : "#475467" }}>
          Context: {adminContextSummary}
        </div>
      </div>

      <label style={{ display: "grid", gap: 4 }}>
        <span style={{ fontWeight: 600 }}>Admin Secret</span>
        <input
          type="password"
          value={adminSecret}
          onChange={(event) => setAdminSecret(event.target.value)}
          placeholder="Enter BENTLEY_WEBHOOK_MANAGEMENT_SECRET"
          style={{
            width: "100%",
            padding: 8,
            border: "1px solid #c8ccd0",
            borderRadius: 4,
            fontSize: 12,
          }}
        />
      </label>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <label style={{ display: "grid", gap: 4 }}>
          <span style={{ fontWeight: 600 }}>iTwin ID</span>
          <input
            type="text"
            value={iTwinId}
            onChange={(event) => setITwinId(event.target.value)}
            style={{
              width: "100%",
              padding: 8,
              border: "1px solid #c8ccd0",
              borderRadius: 4,
              fontSize: 12,
            }}
          />
        </label>
        <label style={{ display: "grid", gap: 4 }}>
          <span style={{ fontWeight: 600 }}>iModel ID</span>
          <input
            type="text"
            value={iModelId}
            onChange={(event) => setIModelId(event.target.value)}
            style={{
              width: "100%",
              padding: 8,
              border: "1px solid #c8ccd0",
              borderRadius: 4,
              fontSize: 12,
            }}
          />
        </label>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        <button
          type="button"
          onClick={() => void loadDiagnostics()}
          disabled={isLoadingDiagnostics || activeAction.length > 0 || !canLoadDiagnostics}
          style={{ padding: "8px 10px", borderRadius: 4, cursor: "pointer" }}
        >
          {isLoadingDiagnostics ? "Loading..." : "Load Diagnostics"}
        </button>
        <button
          type="button"
          onClick={() => void runAction("poll", "/changeset-polling/health/run")}
          disabled={activeAction.length > 0 || !adminSecret.trim()}
          style={{ padding: "8px 10px", borderRadius: 4, cursor: "pointer" }}
        >
          {activeAction === "poll" ? "Running..." : "Run Poll Now"}
        </button>
        <button
          type="button"
          onClick={() =>
            void runAction("seed", "/monitoring/seed-baseline", {
              iTwinId,
              iModelId,
            })
          }
          disabled={activeAction.length > 0 || !adminSecret.trim() || !iTwinId.trim() || !iModelId.trim()}
          style={{ padding: "8px 10px", borderRadius: 4, cursor: "pointer" }}
        >
          {activeAction === "seed" ? "Seeding..." : "Seed Baseline"}
        </button>
        <button
          type="button"
          onClick={() =>
            void runAction("webhook", "/bentley/webhooks/sync", {
              iTwinId,
              forceRecreate: forceWebhookRecreate,
              omitSecret: omitWebhookSecret,
              ...(useTapCallback && tapCallbackUrl ? { callbackUrl: tapCallbackUrl } : {}),
            })
          }
          disabled={activeAction.length > 0 || !adminSecret.trim() || !iTwinId.trim()}
          style={{ padding: "8px 10px", borderRadius: 4, cursor: "pointer" }}
        >
          {activeAction === "webhook" ? "Syncing..." : "Webhook Sync"}
        </button>
      </div>

      <label style={{ display: "flex", alignItems: "center", gap: 8, color: "#475467" }}>
        <input
          type="checkbox"
          checked={forceWebhookRecreate}
          onChange={(event) => setForceWebhookRecreate(Boolean(event.target.checked))}
        />
        <span>Force recreate webhook on next sync</span>
      </label>

      <label style={{ display: "flex", alignItems: "center", gap: 8, color: "#475467" }}>
        <input
          type="checkbox"
          checked={useTapCallback}
          onChange={(event) => setUseTapCallback(Boolean(event.target.checked))}
        />
        <span>Use tap callback on next sync</span>
      </label>

      <label style={{ display: "flex", alignItems: "center", gap: 8, color: "#475467" }}>
        <input
          type="checkbox"
          checked={omitWebhookSecret}
          onChange={(event) => setOmitWebhookSecret(Boolean(event.target.checked))}
        />
        <span>Omit secret on next sync</span>
      </label>

      {useTapCallback && tapCallbackUrl && (
        <div style={{ color: "#475467" }}>
          Tap callback: <code>{tapCallbackUrl}</code>
        </div>
      )}

      {error && (
        <div
          style={{
            color: "#b42318",
            background: "#fef3f2",
            border: "1px solid #fecdca",
            borderRadius: 4,
            padding: 8,
            whiteSpace: "pre-wrap",
          }}
        >
          {error}
        </div>
      )}

      {diagnostics && (
        <div style={{ display: "grid", gap: 10 }}>
          <div
            style={{
              border: `1px solid ${statusColor}`,
              background: `${statusColor}12`,
              borderRadius: 6,
              padding: 10,
            }}
          >
            <div
              style={{
                display: "inline-block",
                padding: "2px 8px",
                borderRadius: 999,
                background: statusColor,
                color: "#fff",
                fontWeight: 600,
                textTransform: "uppercase",
                fontSize: 11,
                marginBottom: 8,
              }}
            >
              {status || "unknown"}
            </div>
            <div style={{ fontWeight: 600 }}>{diagnostics.summary ?? "No summary returned."}</div>
          </div>

          <div style={{ display: "grid", gap: 6 }}>
            <div style={{ fontWeight: 600 }}>Signals</div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                gap: 6,
              }}
            >
              {signalEntries.map(([key, value]) => (
                <div
                  key={key}
                  style={{
                    border: "1px solid #d0d5dd",
                    borderRadius: 4,
                    padding: 8,
                    background: "#fff",
                  }}
                >
                  <div style={{ color: "#475467", fontSize: 11, textTransform: "uppercase" }}>{key}</div>
                  <div style={{ fontWeight: 600 }}>{formatSignalValue(value)}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: "grid", gap: 4 }}>
            <div style={{ fontWeight: 600 }}>Current State</div>
            <div>Subscription ID: {diagnostics.subscription?.subscriptionId ?? "none"}</div>
            <div>Latest visible changeset: {diagnostics.bentleyServiceApp?.latestVisibleChangeset?.changesetIndex ?? "n/a"} ({diagnostics.bentleyServiceApp?.latestVisibleChangeset?.changesetId ?? "n/a"})</div>
            <div>Baseline seen: {diagnostics.subscription?.lastSeenChangesetIndex ?? "n/a"} ({diagnostics.subscription?.lastSeenChangesetId ?? "n/a"})</div>
            <div>Baseline processed: {diagnostics.subscription?.lastProcessedChangesetIndex ?? "n/a"} ({diagnostics.subscription?.lastProcessedChangesetId ?? "n/a"})</div>
            <div>Latest event source: {diagnostics.latestReceivedEvent?.webhookId ?? "none"}</div>
            <div>Latest delta job: {diagnostics.latestDeltaJob?.status ?? "none"}</div>
          </div>

          {recommendations.length > 0 && (
            <div style={{ display: "grid", gap: 4 }}>
              <div style={{ fontWeight: 600 }}>Recommendations</div>
              {recommendations.map((recommendation, index) => (
                <div key={`${index}-${recommendation}`} style={{ color: "#475467" }}>
                  {recommendation}
                </div>
              ))}
            </div>
          )}

          <details>
            <summary style={{ cursor: "pointer", fontWeight: 600 }}>Raw Diagnostics JSON</summary>
            <pre
              style={{
                marginTop: 8,
                maxHeight: 260,
                overflow: "auto",
                background: "#0f172a",
                color: "#e5e7eb",
                padding: 12,
                borderRadius: 6,
                fontSize: 11,
              }}
            >
              {JSON.stringify(diagnostics, null, 2)}
            </pre>
          </details>
        </div>
      )}

      {lastActionPayload && (
        <details>
          <summary style={{ cursor: "pointer", fontWeight: 600 }}>Last Action Response</summary>
          <pre
            style={{
              marginTop: 8,
              maxHeight: 220,
              overflow: "auto",
              background: "#111827",
              color: "#e5e7eb",
              padding: 12,
              borderRadius: 6,
              fontSize: 11,
            }}
          >
            {JSON.stringify(lastActionPayload, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
};
